import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { UserPlus, Info, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/hooks/useAuth";
import { useActiveAddons } from "@/hooks/useActiveAddons";

const ROLE_ADDON_MAP: Record<string, string> = {
  catering: "Catering Dashboard",
  assets: "Assets Dashboard",
  communications: "Communications Dashboard",
  community: "Community Dashboard",
  school_liaison: "School Liaison Dashboard",
  shop: "Shop",
};

interface MemberInviteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}
const MAIN_ROLES = {
  chair: {
    label: "Chair",
    hasDashboard: true,
    description: "Leads the PTA, chairs meetings, and oversees all operations"
  },
  treasurer: {
    label: "Treasurer",
    hasDashboard: true,
    description: "Manages finances, budgets, and financial reporting"
  },
  secretary: {
    label: "Secretary",
    hasDashboard: true,
    description: "Records minutes, manages communications, and handles admin"
  },
  trustee: {
    label: "Trustee",
    hasDashboard: true,
    description: "Provides governance oversight and strategic guidance"
  }
};
const ADDITIONAL_ROLES = {
  catering: {
    label: "Catering",
    hasDashboard: true,
    description: "Organizes refreshments and catering for PTA events",
    requiresAddon: false
  },
  communications: {
    label: "Communications",
    hasDashboard: true,
    description: "Manages newsletters, social media, and public relations",
    requiresAddon: false
  },
  school_liaison: {
    label: "School Liaison",
    hasDashboard: true,
    description: "Liaises between school and PTA, manages goals",
    requiresAddon: false
  },
  assets: {
    label: "Assets",
    hasDashboard: true,
    description: "Manages PTA equipment, inventory, and physical resources",
    requiresAddon: false
  },
  community: {
    label: "Community",
    hasDashboard: true,
    description: "Builds relationships with parents and local community",
    requiresAddon: false
  },
  occasional_helper: {
    label: "Occasional Helper",
    hasDashboard: false,
    description: "Volunteers for specific events when available",
    requiresAddon: false
  },
  shop: {
    label: "Shop",
    hasDashboard: true,
    description: "Manages the online shop and inventory",
    requiresAddon: true
  },
  custom: {
    label: "Custom Role",
    hasDashboard: false,
    description: "Create a custom role with tasks only",
    requiresAddon: false
  }
};

export default function MemberInviteModal({
  open,
  onOpenChange,
  onSuccess
}: MemberInviteModalProps) {
  const {
    subscription,
    user
  } = useAuth();
  const { hasAddon, activeAddonNames } = useActiveAddons();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [customRoleName, setCustomRoleName] = useState("");
  const [members, setMembers] = useState<any[]>([]);
  const isStandardPlan = subscription?.plan === 'standard';

  const isRoleVisible = (roleKey: string): boolean => {
    if (isStandardPlan) return true;
    const addonName = ROLE_ADDON_MAP[roleKey];
    if (!addonName) return true; // core roles always visible
    return hasAddon(addonName);
  };
  const [mainRoles, setMainRoles] = useState<{
    [key: string]: boolean;
  }>({
    chair: false,
    treasurer: false,
    secretary: false,
    trustee: false
  });

  // Handle Chair/Treasurer mutual exclusivity
  const handleMainRoleToggle = (role: string, checked: boolean) => {
    if (checked) {
      if (role === 'chair') {
        setMainRoles(prev => ({
          ...prev,
          chair: true,
          treasurer: false
        }));
      } else if (role === 'treasurer') {
        setMainRoles(prev => ({
          ...prev,
          treasurer: true,
          chair: false
        }));
      } else {
        setMainRoles(prev => ({
          ...prev,
          [role]: true
        }));
      }
    } else {
      setMainRoles(prev => ({
        ...prev,
        [role]: false
      }));
    }
  };
  const [additionalRoles, setAdditionalRoles] = useState<{
    [key: string]: boolean;
  }>({
    catering: false,
    communications: false,
    school_liaison: false,
    assets: false,
    community: false,
    occasional_helper: false,
    shop: false,
    custom: false
  });
  const [dashboardEnabled, setDashboardEnabled] = useState<{
    [key: string]: boolean;
  }>({});
  const [deputyEnabled, setDeputyEnabled] = useState<{
    [key: string]: boolean;
  }>({});
  const [deputyPrimary, setDeputyPrimary] = useState<{
    [key: string]: string;
  }>({});
  const [additionalAccess, setAdditionalAccess] = useState({
    documents: false,
    expenses: false
  });
  const [documentPermission, setDocumentPermission] = useState<"read" | "edit">("read");
  
  
  useEffect(() => {
    if (open) {
      fetchMembers();
    }
  }, [open]);
  const fetchMembers = async () => {
    // Get current user's PTA from invite modal context
    const {
      data: userData
    } = await supabase.from("members").select("pta_id").eq("user_id", user?.id).eq("is_pta_admin", true).single();
    if (!userData?.pta_id) {
      setMembers([]);
      return;
    }

    // Fetch members from the same PTA (without user_roles to avoid join errors)
    const {
      data
    } = await supabase.from("members").select(`
        id,
        full_name,
        email,
        role
      `).eq("pta_id", userData.pta_id).order("full_name");
    setMembers(data || []);
  };
  const resetForm = () => {
    setStep(1);
    setEmail("");
    setFullName("");
    setCustomRoleName("");
    setMainRoles({
      chair: false,
      treasurer: false,
      secretary: false,
      trustee: false
    });
    setAdditionalRoles({
      catering: false,
      communications: false,
      school_liaison: false,
      assets: false,
      community: false,
      occasional_helper: false,
      shop: false,
      custom: false
    });
    setDashboardEnabled({});
    setDeputyEnabled({});
    setDeputyPrimary({});
    setAdditionalAccess({
      documents: false,
      expenses: false
    });
    setDocumentPermission("read");
  };
  const validateStep1 = async () => {
    if (!email || !fullName) {
      toast({
        title: "Required Fields",
        description: "Please fill in email and full name",
        variant: "destructive"
      });
      return false;
    }

    // Check if email already exists in the PTA
    const { data: userData } = await supabase
      .from("members")
      .select("pta_id")
      .eq("user_id", user?.id)
      .eq("is_pta_admin", true)
      .single();

    if (userData?.pta_id) {
      const { data: existingMember } = await supabase
        .from("members")
        .select("id")
        .eq("email", email.toLowerCase().trim())
        .eq("pta_id", userData.pta_id)
        .maybeSingle();

      if (existingMember) {
        toast({
          title: "Email Already Exists",
          description: "This email is already registered for a member in your PTA",
          variant: "destructive"
        });
        return false;
      }
    }

    return true;
  };
  const validateStep2 = () => {
    const hasMainRole = Object.values(mainRoles).some(v => v);
    const hasAdditionalRole = Object.values(additionalRoles).some(v => v);
    const hasOccasionalHelper = additionalRoles.occasional_helper;
    if (!hasMainRole && !hasAdditionalRole) {
      toast({
        title: "No Role Selected",
        description: "Please select at least one role",
        variant: "destructive"
      });
      return false;
    }
    if (mainRoles.chair && mainRoles.treasurer) {
      toast({
        title: "Invalid Selection",
        description: "A member cannot be both Chair and Treasurer",
        variant: "destructive"
      });
      return false;
    }
    if (hasOccasionalHelper && hasMainRole) {
      toast({
        title: "Invalid Selection",
        description: "Occasional helpers cannot have main roles",
        variant: "destructive"
      });
      return false;
    }
    if (additionalRoles.custom && !customRoleName) {
      toast({
        title: "Custom Role Name Required",
        description: "Please enter a name for the custom role",
        variant: "destructive"
      });
      return false;
    }

    // Validate deputy selections
    const selectedRoles = [...Object.entries(mainRoles).filter(([_, v]) => v).map(([k]) => k), ...Object.entries(additionalRoles).filter(([_, v]) => v).map(([k]) => k)];
    for (const role of selectedRoles) {
      if (deputyEnabled[role] && !deputyPrimary[role]) {
        toast({
          title: "Deputy Primary Required",
          description: `Please select a primary member for deputy ${role} role`,
          variant: "destructive"
        });
        return false;
      }
    }
    return true;
  };
  const handleSubmit = async () => {
    if (!validateStep2()) return;

    // Get current user's PTA
    const {
      data: userData
    } = await supabase.from("members").select("pta_id").eq("user_id", user?.id).eq("is_pta_admin", true).single();
    if (!userData?.pta_id) {
      toast({
        title: "Error",
        description: "Could not find your PTA. Please contact support.",
        variant: "destructive"
      });
      return;
    }
    const selectedRoles = [...Object.entries(mainRoles).filter(([_, v]) => v).map(([k]) => k), ...Object.entries(additionalRoles).filter(([_, v]) => v).map(([k]) => k)];

    // Create member
    const {
      data: member,
      error: memberError
    } = await supabase.from("members").insert({
      pta_id: userData.pta_id,
      email,
      full_name: fullName,
      custom_role_name: additionalRoles.custom ? customRoleName : null,
      role: selectedRoles.map(r => {
        if (r === 'custom') return customRoleName;
        return ADDITIONAL_ROLES[r as keyof typeof ADDITIONAL_ROLES]?.label || MAIN_ROLES[r as keyof typeof MAIN_ROLES]?.label || r;
      }).join(", "),
      status: "active",
    }).select().single();
    if (memberError) {
      toast({
        title: "Error",
        description: memberError.message,
        variant: "destructive"
      });
      return;
    }

    // Add user roles ONLY if member has a user_id (actual auth user)
    // Filter out 'custom' and 'site_admin' - site_admin can only be assigned by site admins
    if (selectedRoles.length > 0 && member.user_id) {
      const {
        error: rolesError
      } = await supabase.from("user_roles").insert(
        selectedRoles
          .filter(r => r !== 'custom' && r !== 'site_admin')
          .map(role => ({
            user_id: member.user_id,
            role: role as any
          }))
      );
      if (rolesError) {
        console.error("Roles error:", rolesError);
        // Don't fail the whole operation if roles can't be added
      }
    }

    // Add deputy relationships
    for (const role of selectedRoles) {
      if (deputyEnabled[role] && deputyPrimary[role]) {
        await supabase.from("deputy_roles").insert({
          deputy_member_id: member.id,
          primary_member_id: deputyPrimary[role],
          role: role as any
        });
      }
    }

    // Add additional access
    const accessToAdd = [];
    if (additionalAccess.documents) {
      accessToAdd.push({
        member_id: member.id,
        access_type: "documents",
        permission_level: documentPermission,
        dashboard_enabled: true
      });
    }
    if (additionalAccess.expenses) {
      accessToAdd.push({
        member_id: member.id,
        access_type: "expenses",
        permission_level: null,
        dashboard_enabled: true
      });
    }

    // Add dashboard enabled settings for roles with dashboards
    for (const role of selectedRoles) {
      const roleInfo = MAIN_ROLES[role as keyof typeof MAIN_ROLES] || ADDITIONAL_ROLES[role as keyof typeof ADDITIONAL_ROLES];
      if (roleInfo?.hasDashboard && dashboardEnabled[role] !== undefined) {
        accessToAdd.push({
          member_id: member.id,
          access_type: `dashboard_${role}`,
          permission_level: null,
          dashboard_enabled: dashboardEnabled[role]
        });
      }
    }
    if (accessToAdd.length > 0) {
      await supabase.from("member_additional_access").insert(accessToAdd);
    }

    // Get PTA name for invitation email
    const { data: ptaData } = await supabase
      .from("ptas")
      .select("name")
      .eq("id", userData.pta_id)
      .single();

    // Send invitation email
    try {
      const { error: inviteError } = await supabase.functions.invoke("send-member-invitation", {
        body: {
          memberId: member.id,
          email: email,
          fullName: fullName,
          ptaName: ptaData?.name || "PTA Connect"
        }
      });

      if (inviteError) {
        console.error("Error sending invitation:", inviteError);
        toast({
          title: "Member Created",
          description: `${fullName} has been added, but the invitation email failed to send. You may need to share the invitation link manually.`,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Success",
          description: `${fullName} has been added and an invitation email has been sent`
        });
      }
    } catch (error) {
      console.error("Invitation email error:", error);
      toast({
        title: "Member Created",
        description: `${fullName} has been added, but the invitation email failed to send`,
        variant: "destructive"
      });
    }

    resetForm();
    onSuccess();
    onOpenChange(false);
  };
  const hasOccasionalHelper = additionalRoles.occasional_helper;
  const hasMainRole = Object.values(mainRoles).some(v => v);
  const selectedRoles = [...Object.entries(mainRoles).filter(([_, v]) => v).map(([k]) => k), ...Object.entries(additionalRoles).filter(([_, v]) => v).map(([k]) => k)];

  // Check which roles can have deputies (roles where at least one member already has it)
  const getRoleMembers = (role: string) => {
    return members.filter(m => m.role && m.role.toLowerCase().includes(role.toLowerCase()));
  };
  return <Dialog open={open} onOpenChange={open => {
    if (!open) resetForm();
    onOpenChange(open);
  }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Invite New Member - Step {step} of 3</DialogTitle>
          <DialogDescription>
            {step === 1 && "Enter basic member information"}
            {step === 2 && "Select roles and configure permissions"}
            {step === 3 && "Configure additional access"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Step 1: Basic Info */}
          {step === 1 && <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="member-name">Full Name *</Label>
                <Input id="member-name" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="John Smith" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="member-email">Email Address *</Label>
                <Input id="member-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="john@example.com" />
              </div>
            </div>}

          {/* Step 2: Roles */}
          {step === 2 && <TooltipProvider>
              <div className="space-y-6">
                {/* Main Roles */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm">Main Roles</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(MAIN_ROLES).map(([key, role], index) => {
                  const roleMembers = getRoleMembers(key);
                  const canBeDeputy = roleMembers.length > 0;
                  const isLeftColumn = index % 2 === 0;
                  return <div key={key} className="space-y-2 p-3 border rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Label className="cursor-pointer">{role.label}</Label>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-3 w-3 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent side={isLeftColumn ? "right" : "left"} align="start" sideOffset={8} className="max-w-xs">
                                  <p className="text-sm">{role.description}</p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                            <Switch checked={mainRoles[key]} onCheckedChange={checked => handleMainRoleToggle(key, checked)} disabled={hasOccasionalHelper} />
                          </div>
                          
                          {mainRoles[key] && role.hasDashboard && <div className="ml-4 space-y-2 text-sm">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1">
                                  <span className="text-muted-foreground">Dashboard</span>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Info className="h-3 w-3 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent side={isLeftColumn ? "right" : "left"} align="start" sideOffset={8}>
                                      <p className="text-sm">Enable or disable dashboard access for this role</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </div>
                                <Switch checked={dashboardEnabled[key] !== false} onCheckedChange={checked => setDashboardEnabled(prev => ({
                          ...prev,
                          [key]: checked
                        }))} />
                              </div>
                              
                              {/* Trustee role doesn't have deputy option */}
                              {key !== 'trustee' && <>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1">
                                      <span className="text-muted-foreground">Deputy</span>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Info className="h-3 w-3 text-muted-foreground" />
                                        </TooltipTrigger>
                                        <TooltipContent side={isLeftColumn ? "right" : "left"} align="start" sideOffset={8}>
                                          <p className="text-sm">Deputy shares dashboard data with the primary role holder</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </div>
                                    <Switch checked={deputyEnabled[key] || false} onCheckedChange={checked => setDeputyEnabled(prev => ({
                            ...prev,
                            [key]: checked
                          }))} disabled={!canBeDeputy} />
                                  </div>
                                  
                                  {deputyEnabled[key] && canBeDeputy && <Select value={deputyPrimary[key]} onValueChange={value => setDeputyPrimary(prev => ({
                          ...prev,
                          [key]: value
                        }))}>
                                      <SelectTrigger className="h-8">
                                        <SelectValue placeholder="Select primary" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {roleMembers.map(m => <SelectItem key={m.id} value={m.id}>
                                            {m.full_name}
                                          </SelectItem>)}
                                      </SelectContent>
                                    </Select>}
                                </>}
                            </div>}
                        </div>;
                })}
                  </div>
                </div>

                {/* Additional Roles */}
                <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-sm">Additional Roles</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(ADDITIONAL_ROLES)
                        .filter(([key]) => isRoleVisible(key))
                        .map(([key, role], index) => {
                  const roleMembers = getRoleMembers(key);
                  const canBeDeputy = roleMembers.length > 0;
                  const disabled = (key === "occasional_helper" ? hasMainRole : hasOccasionalHelper);
                  const isLeftColumn = index % 2 === 0;

                  return <div key={key} className="space-y-2 p-3 border rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Label className="cursor-pointer">{role.label}</Label>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Info className="h-3 w-3 text-muted-foreground" />
                                  </TooltipTrigger>
                                  <TooltipContent side={isLeftColumn ? "right" : "left"} align="start" sideOffset={8} className="max-w-xs">
                                    <p className="text-sm">{role.description}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                              <Switch checked={additionalRoles[key]} onCheckedChange={checked => setAdditionalRoles(prev => ({
                        ...prev,
                        [key]: checked
                      }))} disabled={disabled} />
                            </div>
                            
                            {additionalRoles[key] && key === 'custom' && <Input placeholder="Enter role name" value={customRoleName} onChange={e => setCustomRoleName(e.target.value)} className="h-8" />}
                            
                            {additionalRoles[key] && role.hasDashboard && key !== 'custom' && <div className="ml-4 space-y-2 text-sm">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-1">
                                    <span className="text-muted-foreground">Dashboard</span>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Info className="h-3 w-3 text-muted-foreground" />
                                      </TooltipTrigger>
                                      <TooltipContent side={isLeftColumn ? "right" : "left"} align="start" sideOffset={8}>
                                        <p className="text-sm">Enable or disable dashboard access for this role</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </div>
                                  <Switch checked={dashboardEnabled[key] !== false} onCheckedChange={checked => setDashboardEnabled(prev => ({
                          ...prev,
                          [key]: checked
                        }))} />
                                </div>
                                
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-1">
                                    <span className="text-muted-foreground">Deputy</span>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Info className="h-3 w-3 text-muted-foreground" />
                                      </TooltipTrigger>
                                      <TooltipContent side={isLeftColumn ? "right" : "left"} align="start" sideOffset={8}>
                                        <p className="text-sm">Deputy shares dashboard data with the primary role holder</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </div>
                                  <Switch checked={deputyEnabled[key] || false} onCheckedChange={checked => setDeputyEnabled(prev => ({
                          ...prev,
                          [key]: checked
                        }))} disabled={!canBeDeputy} />
                                </div>
                                
                                {deputyEnabled[key] && canBeDeputy && <Select value={deputyPrimary[key]} onValueChange={value => setDeputyPrimary(prev => ({
                        ...prev,
                        [key]: value
                      }))}>
                                    <SelectTrigger className="h-8">
                                      <SelectValue placeholder="Select primary" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {roleMembers.map(m => <SelectItem key={m.id} value={m.id}>
                                          {m.full_name}
                                        </SelectItem>)}
                                    </SelectContent>
                                  </Select>}
                              </div>}
                          </div>;
                })}
                    </div>
                  </div>
              </div>
            </TooltipProvider>}

          {/* Step 3: Additional Access */}
          {step === 3 && <TooltipProvider>
              <div className="space-y-4">
                <h3 className="font-semibold text-sm">Additional Access</h3>
                
                <div className="space-y-3">
                  <div className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Label>Documents</Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3 w-3 text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent side="right" align="start" sideOffset={8}>
                            <p className="text-sm">Grant access to view and/or edit documents</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <Switch checked={additionalAccess.documents} onCheckedChange={checked => setAdditionalAccess(prev => ({
                    ...prev,
                    documents: checked
                  }))} disabled={hasOccasionalHelper} />
                    </div>
                    {additionalAccess.documents && <div className="mt-3 ml-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1">
                            <span className="text-sm text-muted-foreground">Write Access</span>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-3 w-3 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent side="right" align="start" sideOffset={8}>
                                <p className="text-sm">Allow editing documents (off = read-only)</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                          <Switch checked={documentPermission === "edit"} onCheckedChange={checked => setDocumentPermission(checked ? "edit" : "read")} />
                        </div>
                      </div>}
                  </div>

                  <div className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Label>Expenses</Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3 w-3 text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent side="right" align="start" sideOffset={8}>
                            <p className="text-sm">Show claim expense button on dashboard</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <Switch checked={additionalAccess.expenses} onCheckedChange={checked => setAdditionalAccess(prev => ({
                    ...prev,
                    expenses: checked
                  }))} disabled={hasOccasionalHelper} />
                    </div>
                  </div>
                </div>
                
              </div>
            </TooltipProvider>}

          {/* Navigation Buttons */}
          <div className="flex gap-2">
            {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)} className="flex-1">
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back
              </Button>}
            {step < 3 ? <Button onClick={async () => {
            if (step === 1 && await validateStep1()) setStep(2);
            if (step === 2 && validateStep2()) setStep(3);
          }} className="flex-1">
                Next
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button> : <Button onClick={handleSubmit} className="flex-1">
                <UserPlus className="h-4 w-4 mr-2" />
                Add Member
              </Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>;
}